import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PasswordForm } from "@/components/PasswordForm";

// First stop after accepting an invite (or signing in with a temporary password): pick the
// password you'll use from now on. Rendered inside <Protected>, so a session already exists.
export default function CreatePassword() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = (params.get("next") || "").startsWith("/dashboard") ? params.get("next") : "/dashboard";
  const forced = !!user?.must_change_password;

  const done = () => {
    toast.success("Password saved — from now on, sign in with your email and this password.");
    navigate(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-sand-200 p-8" data-testid="create-password-page">
        <div className="mx-auto h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center mb-4">
          <KeyRound className="h-6 w-6 text-redline-500" strokeWidth={1.75} />
        </div>
        <h1 className="font-heading font-bold text-xl text-ink text-center">{forced ? "Replace your temporary password" : "Create your password"}</h1>
        <p className="text-sm text-muted-foreground mt-2 text-center">
          {forced
            ? "The password you were given works once. Pick one only you know to keep going."
            : <>You're in as <span className="font-semibold text-ink">{user?.email}</span>. Set a password now and you can sign in from any device — no emailed links.</>}
        </p>
        <div className="mt-6">
          <PasswordForm onDone={done} submitLabel="Save password and continue" testPrefix="create-password" />
        </div>
        {!forced && (
          <button type="button" data-testid="create-password-skip" onClick={() => navigate(next, { replace: true })}
            className="block w-full text-center mt-4 text-sm font-semibold text-ink/50 hover:text-ink transition-colors">Skip for now</button>
        )}
      </div>
    </div>
  );
}
